import { useMemo, useState } from "react";
import { useSetCoaches, useTrainers } from "../lib/queries";
import { Loading } from "./Loading";
import { useToast } from "./Toast";

interface Trainer { id: string; name: string; is_active: boolean }

interface Props {
  participant: { id: string; name: string; coach_ids: string[] };
  onClose: () => void;
  onDone?: () => void;
}

export function CoachPickerSheet({ participant, onClose, onDone }: Props) {
  const trainersQ = useTrainers();
  const setCoaches = useSetCoaches();
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set(participant.coach_ids));
  const [busy, setBusy] = useState(false);

  const list = useMemo(
    () => ((trainersQ.data ?? []) as Trainer[]).filter((t) => t.is_active),
    [trainersQ.data],
  );

  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const submit = async () => {
    setBusy(true);
    try {
      await setCoaches.mutateAsync({
        participant_id: participant.id,
        trainer_ids: [...selected],
      });
      toast.success("Тренерів збережено");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-20 bg-black/40 flex items-end" onClick={onClose}>
      <div className="bg-tg-bg w-full rounded-t-2xl p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold">Тренери для {participant.name}</h2>
        <p className="text-sm text-tg-hint">
          Обери одного або кількох тренерів. Їхні заняття будуть у стрічці, вони отримуватимуть сповіщення про твої записи.
        </p>

        {trainersQ.isLoading ? <Loading /> : (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {list.map((t) => {
              const isSel = selected.has(t.id);
              return (
                <button
                  key={t.id}
                  className={`w-full card text-left flex items-center gap-2 ${isSel ? "ring-2 ring-tg-link" : ""}`}
                  onClick={() => toggle(t.id)}
                >
                  <span className={`w-5 h-5 rounded border-2 ${isSel ? "border-tg-link bg-tg-link" : "border-tg-hint"} flex items-center justify-center text-white text-xs`}>
                    {isSel ? "✓" : ""}
                  </span>
                  <span className="font-medium">{t.name}</span>
                </button>
              );
            })}
            {list.length === 0 && <div className="text-tg-hint text-sm">У клубі поки немає активних тренерів.</div>}
          </div>
        )}

        <div className="flex gap-2">
          <button className="btn-secondary flex-1" onClick={onClose}>Скасувати</button>
          <button className="btn-primary flex-1" disabled={busy} onClick={submit}>Зберегти</button>
        </div>
      </div>
    </div>
  );
}
